// Location storage types
export interface StoredLocation {
  latitude: number
  longitude: number
  label: string // "10001" or "Current Location" or "Brooklyn, NY"
  source: 'zip' | 'geolocation' | 'ipinfo'
}

const LOCATION_STORAGE_KEY = 'user-location'

// Get stored location from localStorage
export function getStoredLocation(): StoredLocation | null {
  if (typeof window === 'undefined') return null
  
  try {
    const stored = localStorage.getItem(LOCATION_STORAGE_KEY)
    if (!stored) return null
    
    const parsed = JSON.parse(stored) as StoredLocation
    // Validate the stored data
    if (
      typeof parsed.latitude === 'number' &&
      typeof parsed.longitude === 'number' &&
      typeof parsed.label === 'string' &&
      typeof parsed.source === 'string'
    ) {
      return parsed
    }
    return null
  } catch {
    return null
  }
}

// Save location to localStorage
export function setStoredLocation(location: StoredLocation): void {
  if (typeof window === 'undefined') return
  
  try {
    localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location))
  } catch (error) {
    console.error('[Location] Error saving location:', error)
  }
}

// Clear stored location
export function clearStoredLocation(): void {
  if (typeof window === 'undefined') return
  
  try {
    localStorage.removeItem(LOCATION_STORAGE_KEY)
  } catch (error) {
    console.error('[Location] Error clearing location:', error)
  }
}

// Get location from browser geolocation API
export async function getBrowserLocation(): Promise<{
  latitude: number
  longitude: number
} | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
      },
      () => {
        resolve(null)
      },
      { timeout: 5000 }
    )
  })
}

// Get location from IP address using ipinfo.io (free tier)
export async function getIPLocation(): Promise<{
  latitude: number
  longitude: number
  label?: string
} | null> {
  try {
    const response = await fetch('https://ipinfo.io/json', {
      method: 'GET',
    })

    if (!response.ok) {
      return null
    }

    const data = await response.json()

    if (data.loc) {
      const [lat, lng] = data.loc.split(',')
      // Build a friendly label from city + region (e.g. "San Jose, CA")
      const city: string = data.city || ''
      const region: string = data.region || ''
      const label = city && region ? `${city}, ${region}` : city || region || undefined
      return {
        latitude: parseFloat(lat),
        longitude: parseFloat(lng),
        label,
      }
    }

    return null
  } catch (error) {
    console.error('[Location] Error fetching IP location:', error)
    return null
  }
}

// Get location with fallback: stored → IP-based → default (New York)
// Note: We use IP-based as default rather than browser geolocation 
// because browser geolocation requires user permission and is opt-in via LocationPicker
export async function getLocation(): Promise<{
  latitude: number
  longitude: number
  label?: string
  source?: 'zip' | 'geolocation' | 'ipinfo'
}> {
  // Check for stored location first (user override)
  const stored = getStoredLocation()
  if (stored) {
    console.log('[Location] Using stored location:', stored)
    return stored
  }

  // Fallback to IP-based location
  const ipLoc = await getIPLocation()
  if (ipLoc) {
    console.log('[Location] Using IP-based location:', ipLoc)
    return {
      ...ipLoc,
      label: ipLoc.label || 'Auto-detected',
      source: 'ipinfo',
    }
  }

  // Default to Manhattan, NYC if all else fails
  console.log('[Location] Using default location (NYC)')
  return {
    latitude: 40.7128,
    longitude: -74.006,
    label: 'New York, NY (Default)',
    source: 'ipinfo',
  }
}

// Format date as YYYY-MM-DD for AMC API
export function formatDateForAPI(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
